// useSlotRect — アクティブな「空スロット div」の矩形を計測し、永続 CanvasStage を
// その上に absolute で重ねるための React hook (docs/_wip/modern-layout.md §9.7
// 「DOM 配置 = 計測フローティング・スロット方式」)。
//
// 仕組み:
//   - chrome (Modern/Classic) は canvas 領域を **空スロット div** にする。
//   - RoomLayout は CanvasStage を自分の直下に `position: absolute` で置く。
//   - この hook が「スロット div」と「RoomLayout root (= 絶対配置基準)」の
//     `getBoundingClientRect` を計測し、`computeStageRect` で stage の絶対座標
//     (left/top/width/height) を算出して返す。
//   - 追従: スロット div と root の両方を `ResizeObserver` で監視 + window
//     resize/scroll を listen。さらに「ResizeObserver は ancestor scroll/transform
//     由来の rect 移動を捕捉しない」ため rAF ループでも毎 frame 再計測する
//     (§9.7 要検証「window/パネル resize・splitter・zoom・ancestor scroll で
//      jitter しないか、ResizeObserver + rAF reposition」への回答)。
//   - jitter 抑止: `slotRectEquals` でサブピクセルの揺れを吸収し、有意差のときだけ
//     setState する (= rAF 毎の無駄な再 render を起こさない)。

import { useLayoutEffect, useRef, useState } from 'react';

import { computeStageRect, type SlotRect, slotRectEquals } from './floatingSlot.js';

/**
 * @param slotRef   chrome 側の「空スロット div」への ref (アクティブ chrome が持つ)。
 * @param parentRef CanvasStage の絶対配置基準 (RoomLayout root) への ref。
 * @returns 計測済 stage 矩形。未計測 (ref 未確定) のうちは null。
 */
export const useSlotRect = (
  slotRef: React.RefObject<HTMLElement | null>,
  parentRef: React.RefObject<HTMLElement | null>,
): SlotRect | null => {
  const [rect, setRect] = useState<SlotRect | null>(null);
  // 直近 rect を ref で保持し、rAF 毎の比較で setState を抑止する。
  const lastRectRef = useRef<SlotRect | null>(null);

  // measure はマウント直後 (useLayoutEffect) と各種イベントで呼ぶ。
  // SSR / 非ブラウザ環境では何もしない。
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;

    const measure = (): void => {
      const slot = slotRef.current;
      const parent = parentRef.current;
      if (slot === null || parent === null) return;
      const slotBox = slot.getBoundingClientRect();
      const parentBox = parent.getBoundingClientRect();
      const next = computeStageRect(slotBox, parentBox);
      const prev = lastRectRef.current;
      if (prev !== null && slotRectEquals(prev, next)) return;
      lastRectRef.current = next;
      setRect(next);
    };

    // 初回計測 (レイアウト確定後)。
    measure();

    // ResizeObserver: スロット div と root のサイズ変化 (パネル resize / splitter /
    // window resize に伴う flex 再計算) を捕捉。
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      const slot = slotRef.current;
      const parent = parentRef.current;
      if (slot !== null) ro.observe(slot);
      if (parent !== null) ro.observe(parent);
    }

    // window resize/scroll: ancestor scroll や viewport 変化に追従。
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true); // capture = ancestor scroll も拾う

    // rAF ループ: ResizeObserver が拾えない移動 (zoom transform / ancestor scroll の
    // 連続 / splitter ドラッグ中) を毎 frame 補正する。slotRectEquals で抑止するので
    // 変化が無い限り setState は走らない (= idle 時は cheap な getBoundingClientRect のみ)。
    let raf = 0;
    let running = true;
    const tick = (): void => {
      if (!running) return;
      measure();
      raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);

    return () => {
      running = false;
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      ro?.disconnect();
    };
  }, [slotRef, parentRef]);

  return rect;
};
